import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from '../environments/environment';
import { MIN_INSURANCE_DIFF_AB } from '../form/settings';
import { AGREEMENT_TYPE, Agreement } from '../model/agreements.model';
import { AgentCommision } from '../model/allianz-agents.model';
import { ApiState } from '../model/api-state.model';
import AppError from '../model/app-error';
import { AppType } from '../model/app-type.model';
import { BlocklistPayload } from '../model/async-validation.model';
import { Backoffice } from '../model/backoffice.model';
import { Boundries } from '../model/boundries.model';
import { InsuredImportResponse } from '../model/insured-import.model';
import { InsuredPayload } from '../model/insured.model';
import { PathResignation, PathTravelInPL, PathTripAbroad } from '../model/path.model';
import { PaymentsPolicyPayload } from '../model/payments.model';
import { PersistStorageAgentCommissionSplits } from '../model/persist-storage.model';
import { PolicyholderPayload } from '../model/policyholder.model';
import { LoadPolicyDataResult } from '../model/save-calculation.model';
import { AppState } from '../store';
import * as fromAgreements from '../store/agreements/agreements.reducer';
import * as fromAttachments from '../store/attachments/attachments.reducer';
import * as fromConfig from '../store/config/config.reducer';
import * as fromInsuredImport from '../store/insured-import/insured-import.reducer';
import * as fromPayments from '../store/payments/payments.reducer';
import * as fromProcess from '../store/process/process.reducer';
import * as fromRemote from '../store/remote/remote.reducer';
import * as fromSaveContact from '../store/save-contact/save-contact.reducer';
import { daysDifference, formatApiPhoneNumber, getFirstArrayValue, peselToBirthDate, toDateString } from '../utils';
import isAppAgentOrTravelAgency from '../utils/is-agent-or-travel-agency.util';
import { AsyncEmailValidator } from '../validator/async-email.validator';
import { AsyncPhoneNumberValidator } from '../validator/async-phone-number.validator';
import { AsyncAgentNumberValidator } from '../validator/asyncAgentNumber.validator';
import BirthDateValidator from '../validator/birthDateValidator';
import BirthYearValidator from '../validator/birthYear.validator';
import CityValidator from '../validator/cityValidator';
import EmailValidator from '../validator/emailValidator';
import FirstNameValidator from '../validator/firstNameValidator';
import LastNameValidator from '../validator/lastNameValidator';
import NipValidator from '../validator/nip.validator';
import PassportValidator from '../validator/passport.validator';
import PeselValidator from '../validator/pesel.validator';
import PhoneNumberValidator from '../validator/phone-number.validator';
import PostCodeValidator from '../validator/post-code.validator';
import RegonValidator from '../validator/regon.validator';
import StreetNumberValidator from '../validator/street-number-validator';
import StreetValidator from '../validator/street-validator';
import SumCostValidator from '../validator/sumCostValidator.validator';
import TravelDatesValidator from '../validator/travelDates.validator';
import { ApiService } from './api.service';
import { CacheService } from './cache.service';
import { PersistStorageKeys, PersistStorageService } from './persist-storage.service';
import { ProcessService, ProcessType } from './process.service';

const NUMBER_OF_INSUREDS_INITIAL_VALUE = 1;
const NUMBER_OF_INSUREDS_MIN = 1;
const NUMBER_OF_INSUREDS_MAX = 500;

const STARTING_SUM_OF_TRAVEL_COSTS = 0;

const MIN_TRAVEL_COSTS = 0;

const PRESERVE_DEBOUNCE_TIME = 500;
const BACKEND_ID_DIFF = 1;

const ANNUAL_INCREMENT_YEARS = 1;
const TRAVEL_DATE_TO_DEFAULT_INCREMENT_DAYS = 1;

const STARTING_AGENT_COMMISION = 1;
export const MAIN_AGENT_COMMISSION = 100;

export const SINGLE_INSURED = 1;

const FIRST_ITEM = 1;
const AMOUNT_TO_REMOVE = 1;

const VALIDATION_DEBOUNCE_TIME = 1000;

@Injectable({
  providedIn: 'root',
})
export class FormsService implements OnInit, OnDestroy {
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  isPolandIsCurrentResidingVisible$ = new BehaviorSubject<boolean>(false);
  private _hydrationState$ = new BehaviorSubject<ApiState>(ApiState.idle);
  private _processKind$ = this.store.select(fromProcess.selectProcessKind);
  private _remoteClientEmail$ = this.store.select(fromRemote.selectClientEmail);
  private _remoteId$ = this.store.select(fromRemote.selectRemoteId);
  private _resignationLimitPerInsuredPerson$ = this.store.select(fromConfig.selectConfigResignationLimitPerInsuredPerson);
  private _ngDestroy$ = new Subject<void>();
  private _travelInPl = new FormGroup(
    {
      travelDates: new FormGroup({
        dateFrom: new FormControl('', [Validators.required]),
        dateTo: new FormControl('', [Validators.required]),
      }),
      numberOfInsureds: new FormControl<number>(NUMBER_OF_INSUREDS_INITIAL_VALUE, [
        Validators.required,
        Validators.min(NUMBER_OF_INSUREDS_MIN),
        Validators.max(NUMBER_OF_INSUREDS_MAX),
      ]),
      insuredBirthYear: new FormArray<FormControl<number | null>>([new FormControl(null, [Validators.required, BirthYearValidator])]),
      winterSportsHiRisk: new FormControl<boolean | null>(null, [Validators.required]),
      winterSportsHiRiskForAllInsureds: new FormControl<boolean | null>(null),
      personsWithWinterSportsHiRisk: new FormArray<FormControl<boolean | null>>([new FormControl(false)]),
      withExtremeSports: new FormControl<boolean | null>(null, {
        validators: environment.app === AppType.Agent ? [Validators.required] : [],
      }),
      extremeSportsForAllInsureds: new FormControl<boolean | null>(null),
      personsWithExtremeSports: new FormArray<FormControl<boolean | null>>([new FormControl(false)]),
      phoneNumber: new FormControl('', {
        validators: environment.app === AppType.Direct ? [PhoneNumberValidator] : [PhoneNumberValidator],
        asyncValidators:
          environment.app === AppType.Direct
            ? [(control: AbstractControl<string, string>) => this.asyncPhoneNumberValidator.validate(control)]
            : [],
        updateOn: 'blur',
      }),
    },
    {
      validators: [
        (control) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.winterSportsHiRisk === true &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.numberOfInsureds > SINGLE_INSURED &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.winterSportsHiRiskForAllInsureds === false
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const personsWithWinterSportsHiRisk = control.value.personsWithWinterSportsHiRisk as boolean[];
            if (personsWithWinterSportsHiRisk.some(Boolean)) {
              return null;
            }

            return { noPersonWithWinterSportsHiRisk: true };
          }

          return null;
        },
      ],
    },
  );

  private _tripAbroad = new FormGroup(
    {
      destination: new FormControl<string | null>(null, Validators.required),
      travelDates: new FormGroup({
        dateFrom: new FormControl<string | null>(null, Validators.required),
        dateTo: new FormControl<string | null>(null, Validators.required),
        annual: new FormControl<boolean | null>(null),
        with60DaysReturns: new FormControl<boolean | null>(null),
      }),
      numberOfInsureds: new FormControl<number>(NUMBER_OF_INSUREDS_INITIAL_VALUE, [
        Validators.required,
        Validators.min(NUMBER_OF_INSUREDS_MIN),
        Validators.max(NUMBER_OF_INSUREDS_MAX),
      ]),
      insuredBirthYear: new FormArray([new FormControl<number | null>(null, [Validators.required, BirthYearValidator])]),
      isPolandIsCurrentResiding: new FormControl<boolean | null>(null),
      winterSportsHiRisk: new FormControl<boolean | null>(null, {
        validators: [Validators.required],
      }),
      winterSportsHiRiskForAllInsureds: new FormControl<boolean | null>(null),
      personsWithWinterSportsHiRisk: new FormArray<FormControl<boolean | null>>([new FormControl(false)]),
      withExtremeSports: new FormControl<boolean | null>(null, {
        validators: this.isAppAgentOrTravelAgency ? [Validators.required] : [],
      }),
      extremeSportsForAllInsureds: new FormControl<boolean | null>(null),
      personsWithExtremeSports: new FormArray<FormControl<boolean | null>>([new FormControl(false)]),
      phoneNumber: new FormControl('', {
        validators: environment.app === AppType.Direct ? [PhoneNumberValidator] : [PhoneNumberValidator],
        asyncValidators:
          environment.app === AppType.Direct
            ? [(control: AbstractControl<string, string>) => this.asyncPhoneNumberValidator.validate(control)]
            : [],
        updateOn: 'blur',
      }),
    },
    {
      validators: [
        TravelDatesValidator,
        (control) => {
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.winterSportsHiRisk === true &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.numberOfInsureds > SINGLE_INSURED &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            control.value.winterSportsHiRiskForAllInsureds === false
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const personsWithWinterSportsHiRisk = control.value.personsWithWinterSportsHiRisk as boolean[];
            if (personsWithWinterSportsHiRisk.some(Boolean)) {
              return null;
            }

            return { noPersonWithWinterSportsHiRisk: true };
          }

          return null;
        },
      ],
    },
  );

  private _resignation = new FormGroup({
    destination: new FormControl<string | null>(null, Validators.required),
    travelDates: new FormGroup({
      dateFrom: new FormControl<string | null>(null, Validators.required),
      dateTo: new FormControl<string | null>(null, Validators.required),
    }),
    numberOfInsureds: new FormControl<number>(NUMBER_OF_INSUREDS_INITIAL_VALUE, [
      Validators.required,
      Validators.min(NUMBER_OF_INSUREDS_MIN),
      Validators.max(NUMBER_OF_INSUREDS_MAX),
    ]),
    travelCost: new FormArray<
      FormGroup<{
        travelPurchaseDate: FormControl<string | null>;
        travelCost: FormControl<number | null>;
      }>
    >([]),
    totalTravelPrice: new FormControl<number | null>(null),
    phoneNumber: new FormControl('', {
      validators: environment.app === AppType.Direct ? [PhoneNumberValidator] : [PhoneNumberValidator],
      asyncValidators:
        environment.app === AppType.Direct
          ? [(control: AbstractControl<string, string>) => this.asyncPhoneNumberValidator.validate(control)]
          : [],
      updateOn: 'blur',
    }),
  });
  private _agreementsForm = new FormGroup({
    marketing: new FormGroup<Record<string, FormControl<boolean>>>({}),
    statements: new FormGroup<Record<string, FormControl<boolean>>>({}),
  });

  private _policyholder = new FormGroup({
    isCompany: new FormControl(false, [Validators.required]),
    firstName: new FormControl('', [Validators.required, FirstNameValidator]),
    lastName: new FormControl('', [Validators.required, LastNameValidator]),
    pesel: new FormControl('', {
      validators: [Validators.required, PeselValidator],
      updateOn: 'blur',
    }),
    isForeigner: new FormControl<boolean | null>(null),
    passport: new FormControl('', [PassportValidator]),
    companyName: new FormControl('', []),
    nip: new FormControl('', {
      validators: [NipValidator],
      updateOn: 'blur',
    }),
    regon: new FormControl('', [RegonValidator]),
    phoneNumber: new FormControl('', {
      validators: [Validators.required, PhoneNumberValidator],
      asyncValidators: [(control: AbstractControl<string, string>) => this.asyncPhoneNumberValidator.validate(control)],
      updateOn: 'blur',
    }),
    emailAddress: new FormControl('', {
      validators: [Validators.required, EmailValidator],
      asyncValidators: [(control: AbstractControl<string, string>) => this.asyncEmailValidator.validate(control)],
      updateOn: 'blur',
    }),
    postCode: new FormControl('', [Validators.required, PostCodeValidator]),
    city: new FormControl<string | null>(null, [Validators.required, CityValidator]),
    street: new FormControl<string | null>(null, [Validators.required, StreetValidator]),
    houseNumber: new FormControl<string | null>(null, [Validators.required, StreetNumberValidator]),
    flatNumber: new FormControl<string | null>(null, [StreetNumberValidator]),
    insureds: new FormArray([
      new FormGroup({
        firstName: new FormControl<string | null>('', [Validators.required, FirstNameValidator]),
        lastName: new FormControl<string | null>('', [Validators.required, LastNameValidator]),
        dateOfBirth: new FormControl<string | null>('', [Validators.required, BirthDateValidator]),
        toRemove: new FormControl<boolean | null>(false),
      }),
    ]),
  });

  private _backofficeSection = new FormGroup({
    premiumPercentDecrease: new FormControl<number | null>(null, []),
    premiumPercentIncrease: new FormControl<number | null>(null, []),
    postingOnAgentBehalf: new FormControl<boolean | null>(null, []),
    impersonateAgentNumber: new FormControl<string | null>(null, {
      updateOn: 'blur',
    }),
    agentData: new FormControl(null),
    specialContractTerms: new FormControl<boolean | null>(null, []),
    specialContractTermsText: new FormControl<string | null>(null, []),
  });

  private _agentCommissionSplits = new FormGroup({
    splits: new FormArray([
      new FormGroup({
        agentNumber: new FormControl(
          {
            value: this.persistStorage.getString(PersistStorageKeys.AgentNumber),
            disabled: true,
          },
          {
            validators: [Validators.required],
            asyncValidators: [(control: AbstractControl<string, string>) => this.asyncAgentNumberValidator.validate(control, 'agentData')],
            updateOn: 'blur',
          },
        ),
        commissionPercent: new FormControl({ value: MAIN_AGENT_COMMISSION, disabled: true }),
        agentData: new FormControl({
          value: `${String(this.persistStorage.getString(PersistStorageKeys.GivenName))} ${String(
            this.persistStorage.getString(PersistStorageKeys.FamilyName),
          )}`,
          disabled: true,
        }),
      }),
    ]),
  });

  private _travelInPlInvalid$ = new BehaviorSubject<boolean>(true);
  private _tripAbroadInvalid$ = new BehaviorSubject<boolean>(true);
  private _resignationInvalid$ = new BehaviorSubject<boolean>(true);
  private _policyholderInvalid$ = new BehaviorSubject<boolean>(true);

  private _navButtonPosition$ = new Subject<Boundries>();

  constructor(
    private store: Store<AppState>,
    private persistStorage: PersistStorageService,
    private processService: ProcessService,
    private translateService: TranslateService,
    private cacheService: CacheService,
    private asyncPhoneNumberValidator: AsyncPhoneNumberValidator,
    private asyncAgentNumberValidator: AsyncAgentNumberValidator,
    private asyncEmailValidator: AsyncEmailValidator,
    private apiService: ApiService,
  ) {
    this._resignationLimitPerInsuredPerson$.pipe(filter(Boolean), first()).subscribe(() => {
      this.addNewTravelCost();
    });
    this.store.dispatch(fromInsuredImport.actions.succesfullyImported({ importedToForm: false }));
  }

  get navButtonPosition(): Observable<Boundries> {
    return this._navButtonPosition$.asObservable();
  }

  get hydrationState$(): Observable<ApiState> {
    return this._hydrationState$.asObservable();
  }

  get travelInPl() {
    return this._travelInPl;
  }

  get travelInPlApi(): PathTravelInPL {
    const { value } = this.travelInPl;

    const winterSportsHiRiskForAllInsureds =
      value.winterSportsHiRiskForAllInsureds || value.personsWithWinterSportsHiRisk?.every((selected) => selected) || false;

    const extremeSportsForAllInsureds =
      value.extremeSportsForAllInsureds || value.personsWithExtremeSports?.every((selected) => selected) || false;

    return {
      travelDates: {
        dateFrom: value.travelDates?.dateFrom ?? '',
        dateTo: value.travelDates?.dateTo ?? '',
      },
      travelersPerBirthYear: {
        numberOfInsureds: value.numberOfInsureds ?? NaN,
        insuredBirthYear:
          value.insuredBirthYear?.map((year, index) => ({
            id: index + BACKEND_ID_DIFF,
            birthYear: year ?? NaN,
          })) ?? [],
      },
      coverages: {
        winterSportsHiRisk: {
          selected: value.winterSportsHiRisk ?? false,
          ...(value.winterSportsHiRisk
            ? {
                specifying: {
                  winterSportsHiRiskForAllInsureds,
                  ...(!winterSportsHiRiskForAllInsureds
                    ? {
                        personsWithWinterSportsHiRisk: value.personsWithWinterSportsHiRisk?.map((selected, id) => ({
                          id: id + BACKEND_ID_DIFF,
                          selected: selected ?? false,
                        })),
                      }
                    : {}),
                },
              }
            : {}),
        },
        ...(this.isAppAgentOrTravelAgency
          ? {
              extremeSports: {
                selected: value.withExtremeSports ?? false,
                ...(value.withExtremeSports
                  ? {
                      specifying: {
                        extremeSportsForAllInsureds,
                        ...(!extremeSportsForAllInsureds
                          ? {
                              personsWithExtremeSports: value.personsWithExtremeSports?.map((selected, id) => ({
                                id: id + BACKEND_ID_DIFF,
                                selected: selected ?? false,
                              })),
                            }
                          : {}),
                      },
                    }
                  : {}),
              },
            }
          : {}),
      },
    };
  }

  get policyholder() {
    return this._policyholder;
  }

  get policyholderApi(): PolicyholderPayload {
    const { value } = this.policyholder;

    return {
      isCompany: value.isCompany ?? false,
      isForeigner: value.isForeigner ?? false,
      companyName: value.companyName ?? '',
      pesel: value.pesel ?? '',
      firstName: value.firstName?.trim() ?? '',
      lastName: value.lastName?.trim() ?? '',
      nip: value.nip ?? '',
      regon: value.regon ?? '',
      passport: value.passport ?? '',
      address: {
        postCode: value.postCode ?? '',
        city: value.city?.trim() ?? '',
        street: value.street?.trim() ?? '',
        houseNumber: value.houseNumber?.trim() ?? '',
        flatNumber: value.flatNumber?.trim() ?? '',
      },
      emailAddress: value.emailAddress ?? '',
      phoneNumber: formatApiPhoneNumber(value.phoneNumber),
    };
  }

  get paymentPolicyApi$(): Observable<PaymentsPolicyPayload> {
    return this._remoteId$.pipe(
      takeUntil(this._ngDestroy$),
      first(),
      map((remoteId) => {
        let returnUrl = `${window.location.origin + window.location.pathname}#/${remoteId ? 'remote/' + remoteId : 'page'}/payment`;

        if (!environment.production) {
          returnUrl = this.removePortFromUrl(returnUrl);
        }

        return returnUrl;
      }),
      switchMap((returnUrl) => {
        const { value } = this.policyholder;
        if (value.emailAddress) {
          return of({
            emailAddress: value.emailAddress,
            returnLink: returnUrl,
          });
        }

        return this._remoteClientEmail$.pipe(map((email) => ({ emailAddress: email ?? '', returnLink: returnUrl })));
      }),
    );
  }

  get insuredsApi(): InsuredPayload[] {
    const { value } = this.policyholder;

    const payload: InsuredPayload[] = [];

    value.insureds?.forEach((insured, index) => {
      payload.push({
        id: index + BACKEND_ID_DIFF,
        firstName: insured.firstName ?? '',
        lastName: insured.lastName ?? '',
        dateOfBirth: insured.dateOfBirth ?? '',
      });
    });

    return payload;
  }

  get agreementsApi(): number[] {
    const { value } = this._agreementsForm;

    return Object.values(value).reduce<number[]>((acc, groupedAgreements) => {
      Object.entries(groupedAgreements).forEach(([key, selected]) => {
        if (selected) {
          acc.push(Number(key.replace('agreement.', '')));
        }
      });

      return acc;
    }, []);
  }

  get tripAbroad() {
    return this._tripAbroad;
  }

  get tripAbroadApi(): PathTripAbroad {
    const { value } = this.tripAbroad;

    const winterSportsHiRiskForAllInsureds =
      value.winterSportsHiRiskForAllInsureds || value.personsWithWinterSportsHiRisk?.every((selected) => selected) || false;

    const extremeSportsForAllInsureds =
      value.extremeSportsForAllInsureds || value.personsWithExtremeSports?.every((selected) => selected) || false;

    return {
      destination: {
        countryId: (value.destination ?? '').replace(/[a-z]/gi, ''),
      },
      travelDates: value.travelDates?.annual
        ? {
            dateFrom: value.travelDates.dateFrom ?? '',
            annual: value.travelDates.annual ?? false,
            with60DaysReturns: value.travelDates.with60DaysReturns ?? false,
          }
        : {
            dateFrom: value.travelDates?.dateFrom ?? '',
            dateTo: value.travelDates?.dateTo ?? '',
          },
      travelersPerBirthYear: {
        numberOfInsureds: value.numberOfInsureds ?? NUMBER_OF_INSUREDS_INITIAL_VALUE,
        insuredBirthYear:
          value.insuredBirthYear?.map((birthYear, id) => ({
            id: id + BACKEND_ID_DIFF,
            birthYear: birthYear ?? NaN,
          })) ?? [],
      },
      isPolandIsCurrentResiding: value.isPolandIsCurrentResiding ?? true,
      coverages: {
        winterSportsHiRisk: {
          selected: value.winterSportsHiRisk ?? false,
          ...(value.winterSportsHiRisk
            ? {
                specifying: {
                  winterSportsHiRiskForAllInsureds,
                  ...(!winterSportsHiRiskForAllInsureds
                    ? {
                        personsWithWinterSportsHiRisk: value.personsWithWinterSportsHiRisk?.map((selected, id) => ({
                          id: id + BACKEND_ID_DIFF,
                          selected: selected ?? false,
                        })),
                      }
                    : {}),
                },
              }
            : {}),
        },
        ...(this.isAppAgentOrTravelAgency
          ? {
              extremeSports: {
                selected: value.withExtremeSports ?? false,
                ...(value.withExtremeSports
                  ? {
                      specifying: {
                        extremeSportsForAllInsureds,
                        ...(!extremeSportsForAllInsureds
                          ? {
                              personsWithExtremeSports: value.personsWithExtremeSports?.map((selected, id) => ({
                                id: id + BACKEND_ID_DIFF,
                                selected: selected ?? false,
                              })),
                            }
                          : {}),
                      },
                    }
                  : {}),
              },
            }
          : {}),
      },
    };
  }

  get resignation() {
    return this._resignation;
  }

  get resignationApi(): PathResignation {
    const { value } = this.resignation;

    return {
      destination: {
        countryId: (value.destination ?? '').replace(/[a-z]/gi, ''),
      },
      travelDates: {
        dateTo: value.travelDates?.dateTo ?? '',
        dateFrom: value.travelDates?.dateFrom ?? '',
      },
      numberOfInsureds: value.numberOfInsureds ?? NaN,
      totalTravelPrice: value.totalTravelPrice ?? NaN,
      travelPurchaseDate: value.travelCost ? getFirstArrayValue(value.travelCost)?.travelPurchaseDate ?? '' : '',
    };
  }

  get agreementsForm() {
    return this._agreementsForm;
  }

  get agentCommissionSplits() {
    return this._agentCommissionSplits;
  }

  get agentCommisionSplitsApi(): AgentCommision[] | undefined {
    const { value } = this.agentCommissionSplits;

    return value?.splits?.map(
      (split) =>
        ({
          agentNumber: String(split.agentNumber),
          commissionPercent: Number(split.commissionPercent),
        } as AgentCommision),
    );
  }

  get backofficeSection() {
    return this._backofficeSection;
  }

  get backOfficeSectionApi(): Partial<Backoffice> {
    const { value } = this.backofficeSection;

    const backOfficeBody: Partial<Backoffice> = {};

    if (value.premiumPercentIncrease) {
      backOfficeBody.premiumPercentIncrease = Number(value.premiumPercentIncrease);
    }

    if (value.premiumPercentDecrease) {
      backOfficeBody.premiumPercentDecrease = Number(value.premiumPercentDecrease);
    }

    if (value.specialContractTerms && value.specialContractTermsText) {
      backOfficeBody.specialContractTerms = String(value.specialContractTermsText);
    }

    if (value.impersonateAgentNumber) {
      backOfficeBody.impersonateAgentNumber = String(value.impersonateAgentNumber);
    }

    return backOfficeBody;
  }

  get isPolandIsCurrentResidingVisible(): boolean {
    const { value } = this.tripAbroad;
    const dateFrom = value.travelDates?.dateFrom;
    const isPolandIsCurrentResiding = value.isPolandIsCurrentResiding;

    const daysDiff = dateFrom ? daysDifference(new Date(dateFrom), new Date()) : null;

    if (!daysDiff) {
      return false;
    }

    const forceDisplay = isPolandIsCurrentResiding === false && daysDiff === MIN_INSURANCE_DIFF_AB;
    const tooFewDays = daysDiff < MIN_INSURANCE_DIFF_AB;

    return forceDisplay || tooFewDays;
  }

  checkBlocklist() {
    return merge(this.policyholder.valueChanges, this.policyholder.statusChanges).pipe(
      distinctUntilChanged(),
      debounceTime(VALIDATION_DEBOUNCE_TIME),
      filter(() => this.policyholder.valid),
      map(() => this.policyholder.value),
      map((formValues) => {
        let body: BlocklistPayload = {
          isCompany: formValues.isCompany ?? false,
          isForeigner: formValues.isForeigner ?? false,
          phoneNumber: formValues.phoneNumber ?? '',
          emailAddress: formValues.emailAddress ?? '',
          address: {
            postCode: formValues.postCode ?? '',
            city: formValues.city ?? '',
            street: formValues.street ?? '',
            houseNumber: formValues.houseNumber ?? '',
            flatNumber: formValues.flatNumber ?? undefined,
          },
        };
        if (body.isCompany) {
          body = {
            ...body,
            companyName: formValues.companyName ?? '',
            nip: formValues.nip ?? '',
          };
        } else {
          body = {
            ...body,
            firstName: formValues.firstName ?? '',
            lastName: formValues.lastName ?? '',
          };

          if (body.isForeigner) {
            body = {
              ...body,
              passport: formValues.passport ?? '',
            };
          } else {
            body = {
              ...body,
              pesel: formValues.pesel ?? '',
            };
          }
        }

        return body;
      }),
      tap(() => this.store.dispatch(fromProcess.actions.blockListCheckStart())),
      switchMap((body) =>
        this.apiService.postBlocklistCheck({ policyholder: body }).pipe(
          catchError((error: AppError) => {
            this.store.dispatch(fromProcess.actions.blockListCheckError({ error }));

            return EMPTY;
          }),
        ),
      ),
      tap((result) => {
        this.store.dispatch(fromProcess.actions.blockListCheckSuccess({ success: result }));
        if (!result?.result?.ignore && result?.result?.isBlocked) {
          this.policyholder.setErrors({ blockList: true });
        } else {
          this.policyholder.setErrors(null);
        }
      }),
    );
  }

  getInsuredsSportNames(i: number): string[] {
    const names: string[] = [];
    this._processKind$.pipe(first()).subscribe((kind) => {
      switch (kind) {
        case ProcessType.TRAVELINPL: {
          const formValues = this.travelInPl.value;
          if (
            formValues.winterSportsHiRisk &&
            (formValues.winterSportsHiRiskForAllInsureds ||
              (formValues.personsWithWinterSportsHiRisk && Boolean(formValues.personsWithWinterSportsHiRisk[i])))
          ) {
            names.push('common.sport-names.winterSportsHiRisk');
          }
          if (
            formValues.withExtremeSports &&
            (formValues.extremeSportsForAllInsureds ||
              (formValues.personsWithExtremeSports && Boolean(formValues.personsWithExtremeSports[i])))
          ) {
            names.push('common.sport-names.withExtremeSports');
          }
          break;
        }
        case ProcessType.TRIPABROAD: {
          const formValues = this.tripAbroad.value;
          if (
            formValues.winterSportsHiRisk &&
            (formValues.winterSportsHiRiskForAllInsureds ||
              (formValues.personsWithWinterSportsHiRisk && Boolean(formValues.personsWithWinterSportsHiRisk[i])))
          ) {
            names.push('common.sport-names.winterSportsHiRisk');
          }
          if (
            formValues.withExtremeSports &&
            (formValues.extremeSportsForAllInsureds ||
              (formValues.personsWithExtremeSports && Boolean(formValues.personsWithExtremeSports[i])))
          ) {
            names.push('common.sport-names.withExtremeSports');
          }
          break;
        }
      }
    });

    return names;
  }

  addNewMiddleman() {
    this._agentCommissionSplits.controls.splits.push(
      new FormGroup({
        agentNumber: new FormControl('', {
          validators: [Validators.required],
          asyncValidators: [(control: AbstractControl<string, string>) => this.asyncAgentNumberValidator.validate(control, 'agentData')],
          updateOn: 'blur',
        }),
        commissionPercent: new FormControl(STARTING_AGENT_COMMISION, [Validators.required]),
        agentData: new FormControl(''),
      }),
    );
  }

  removeMiddleman(index: number) {
    this._agentCommissionSplits.controls.splits.removeAt(index);
  }

  setAgreementsForm(agreements: Agreement[], type: AGREEMENT_TYPE, values?: Record<string, boolean>) {
    Object.values(agreements).forEach(({ agreementId, value, isReadOnly, isRequired }) => {
      const validators = [];

      if (isRequired) {
        validators.push(Validators.requiredTrue);
      }

      const name = `agreement.${agreementId}`;
      const valueToSet: boolean = values?.[name] || value;

      this._agreementsForm.controls[type].addControl(
        name,
        new FormControl({ value: valueToSet, disabled: isReadOnly }, validators) as FormControl<boolean>,
      );
    });
  }

  agreementFormAllMarketingValue(next = true) {
    Object.values(this._agreementsForm.controls.marketing.controls).forEach((control) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      control.setValue(next);
    });
  }

  agreementFormAllStatementsValue(next = true) {
    Object.values(this._agreementsForm.controls.statements.controls).forEach((control) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      control.setValue(next);
    });
  }

  ngOnInit(): void {
    this.implementInvalidStep2();
    this.implementInvalidStep4();
    this.implementUpdateInsuredBirthYearControls();
    this.implementUpdateTotalTravelPrice();
    this.implementUpdateAnnualControls();
    this.implementBackofficeSectionValidations();
    this.implementTravelCostValidations();
    this.implementPolicyholderForm();
    this.implementAgentSplitCommisionsForm();
    void this.implementHydrateForm();
    this.implementPreserveForm();
  }

  ngOnDestroy(): void {
    this._ngDestroy$.next();
    this._ngDestroy$.complete();
  }

  setInsuredPersonParams(): void {
    const firstName = this._policyholder.value.firstName;
    const lastName = this._policyholder.value.lastName;
    const pesel = this._policyholder.value.pesel;
    const isForeigner = this._policyholder.value.isForeigner;
    const dateOfBirth = (!isForeigner && pesel ? peselToBirthDate(pesel) : '') || '';

    this._policyholder.controls.insureds.clear();
    const insured = new FormGroup<{
      firstName: FormControl<string | null>;
      lastName: FormControl<string | null>;
      dateOfBirth: FormControl<string | null>;
      toRemove: FormControl<boolean | null>;
    }>({
      firstName: new FormControl<string>(firstName || '', [Validators.required, FirstNameValidator]),
      lastName: new FormControl<string>(lastName || '', [Validators.required, LastNameValidator]),
      dateOfBirth: new FormControl<string>(dateOfBirth, [Validators.required, BirthDateValidator]),
      toRemove: new FormControl<boolean | null>(false),
    });
    this._policyholder.controls.insureds.push(insured);
  }

  addNewTravelCost() {
    this._resignationLimitPerInsuredPerson$.pipe(first()).subscribe(() => {
      const travelCost = new FormGroup({
        travelPurchaseDate: new FormControl<string | null>(null, Validators.required),
        travelCost: new FormControl<number | null>(null, [Validators.required, Validators.min(MIN_TRAVEL_COSTS)]),
      });

      this._resignation.controls.travelCost.push(travelCost);
    });
  }

  removeTravelCost(index: number) {
    this._resignation.controls.travelCost.removeAt(index);
  }

  resetForms() {
    this._tripAbroad.reset();
    this._tripAbroad.controls.numberOfInsureds.setValue(NUMBER_OF_INSUREDS_INITIAL_VALUE);
    this._travelInPl.reset();
    this._travelInPl.controls.numberOfInsureds.setValue(NUMBER_OF_INSUREDS_INITIAL_VALUE);
    this._resignation.reset();
    this._resignation.controls.numberOfInsureds.setValue(NUMBER_OF_INSUREDS_INITIAL_VALUE);
    this._policyholder.reset();
    this.resetPolicyholderInsureds();
    this._backofficeSection.reset();
    this._agentCommissionSplits.reset();
    this.resetAgentCommissionSplits();
    this._agreementsForm.reset();
    Object.values(this._agreementsForm.controls).forEach((control) => {
      const subControlNames = Object.keys(control.controls);

      subControlNames.forEach((subControlName) => {
        control.removeControl(subControlName as never);
      });
    });
  }

  resetApp(): void {
    this.persistStorage.removeAll('forms');
    this.store.dispatch(fromAgreements.actions.reset());
    this.store.dispatch(fromAttachments.actions.reset());
    this.store.dispatch(fromPayments.actions.reset());
    this.store.dispatch(fromProcess.actions.reset());
    this.store.dispatch(fromRemote.actions.reset());
    this.store.dispatch(fromSaveContact.actions.reset());
    this.resetForms();
    this.cacheService.clearCache();
  }

  updateNavButtonPosition(value: Boundries) {
    this._navButtonPosition$.next(value);
  }

  importInsuredData(data: InsuredImportResponse) {
    this._processKind$.pipe(first()).subscribe((kind) => {
      switch (kind) {
        case ProcessType.TRAVELINPL: {
          this.travelInPl.controls.numberOfInsureds.setValue(data.records?.length || null);
          this.setInsuredsBirthYear(this.travelInPl.controls.insuredBirthYear.controls, data);
          this.setInsuredsData(this._policyholder.controls.insureds.controls, data);
          break;
        }
        case ProcessType.TRIPABROAD: {
          this.tripAbroad.controls.numberOfInsureds.setValue(data.records?.length || null);
          this.setInsuredsBirthYear(this.tripAbroad.controls.insuredBirthYear.controls, data);
          this.setInsuredsData(this._policyholder.controls.insureds.controls, data);
          break;
        }
        case ProcessType.RESIGNATION: {
          this.resignation.controls.numberOfInsureds.setValue(data.records?.length || null);
          this.setInsuredsData(this._policyholder.controls.insureds.controls, data);
          break;
        }
      }
    });
  }

  removeInsureds(indexArr: number[]) {
    this._processKind$.pipe(first()).subscribe((kind) => {
      switch (kind) {
        case ProcessType.TRAVELINPL: {
          const numberOfInsureds = this._travelInPl.value.numberOfInsureds
            ? this._travelInPl.value.numberOfInsureds - indexArr.length
            : NUMBER_OF_INSUREDS_INITIAL_VALUE;
          this._travelInPl.controls.numberOfInsureds.setValue(numberOfInsureds, { emitEvent: false });

          const insuredBirthYearFormArray = new FormArray(
            this._travelInPl.controls.insuredBirthYear.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<number | null>(value, [Validators.required, BirthYearValidator])),
          );
          this._travelInPl.setControl('insuredBirthYear', insuredBirthYearFormArray);

          const personsWithExtremeSportsFormArray = new FormArray(
            this._travelInPl.controls.personsWithExtremeSports.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<boolean | null>(value)),
          );
          this._travelInPl.setControl('personsWithExtremeSports', personsWithExtremeSportsFormArray);

          const personsWithWinterSportsHiRiskFormArray = new FormArray(
            this._travelInPl.controls.personsWithWinterSportsHiRisk.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<boolean | null>(value)),
          );
          this._travelInPl.setControl('personsWithWinterSportsHiRisk', personsWithWinterSportsHiRiskFormArray);
          break;
        }
        case ProcessType.TRIPABROAD: {
          const numberOfInsureds = this._tripAbroad.value.numberOfInsureds
            ? this._tripAbroad.value.numberOfInsureds - indexArr.length
            : NUMBER_OF_INSUREDS_INITIAL_VALUE;
          this._tripAbroad.controls.numberOfInsureds.setValue(numberOfInsureds, { emitEvent: false });

          const insuredBirthYearFormArray = new FormArray(
            this._tripAbroad.controls.insuredBirthYear.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<number | null>(value, [Validators.required, BirthYearValidator])),
          );
          this._tripAbroad.setControl('insuredBirthYear', insuredBirthYearFormArray);

          const personsWithExtremeSportsFormArray = new FormArray(
            this._tripAbroad.controls.personsWithExtremeSports.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<boolean | null>(value)),
          );
          this._tripAbroad.setControl('personsWithExtremeSports', personsWithExtremeSportsFormArray);

          const personsWithWinterSportsHiRiskFormArray = new FormArray(
            this._tripAbroad.controls.personsWithWinterSportsHiRisk.value
              .filter((_, i) => !indexArr.includes(i))
              .map((value) => new FormControl<boolean | null>(value)),
          );
          this._tripAbroad.setControl('personsWithWinterSportsHiRisk', personsWithWinterSportsHiRiskFormArray);
          break;
        }
        case ProcessType.RESIGNATION: {
          const numberOfInsureds = this._resignation.value.numberOfInsureds
            ? this._resignation.value.numberOfInsureds - indexArr.length
            : NUMBER_OF_INSUREDS_INITIAL_VALUE;
          this._resignation.controls.numberOfInsureds.setValue(numberOfInsureds, { emitEvent: false });
          break;
        }
      }
    });

    const insuredsFormArray = new FormArray(
      this._policyholder.controls.insureds.value
        .filter((_, i) => !indexArr.includes(i))
        .map(
          (value) =>
            new FormGroup({
              firstName: new FormControl<string | null>(value.firstName || '', [Validators.required, FirstNameValidator]),
              lastName: new FormControl<string | null>(value.lastName || '', [Validators.required, LastNameValidator]),
              dateOfBirth: new FormControl<string | null>(value.dateOfBirth || '', [Validators.required, BirthDateValidator]),
              toRemove: new FormControl<boolean | null>(value.toRemove || false),
            }),
        ),
    );
    this._policyholder.setControl('insureds', insuredsFormArray);
  }

  clearInsuredsToRemoveParam() {
    const insuredsFormArray = new FormArray(
      this._policyholder.controls.insureds.value.map(
        (value) =>
          new FormGroup({
            firstName: new FormControl<string | null>(value.firstName || '', [Validators.required, FirstNameValidator]),
            lastName: new FormControl<string | null>(value.lastName || '', [Validators.required, LastNameValidator]),
            dateOfBirth: new FormControl<string | null>(value.dateOfBirth || '', [Validators.required, BirthDateValidator]),
            toRemove: new FormControl<boolean | null>(false),
          }),
      ),
    );

    this._policyholder.setControl('insureds', insuredsFormArray);
  }

  updateBackofficeSection(data: LoadPolicyDataResult) {
    const { premiumPercentIncrease, premiumPercentDecrease, impersonateAgentNumber, specialContractTerms } = data.backofficeSection;

    if (premiumPercentIncrease) {
      this._backofficeSection.controls.premiumPercentIncrease.setValue(premiumPercentIncrease);
    }

    if (premiumPercentDecrease) {
      this._backofficeSection.controls.premiumPercentDecrease.setValue(premiumPercentDecrease);
    }

    if (impersonateAgentNumber) {
      this._backofficeSection.controls.postingOnAgentBehalf.setValue(true);
      this._backofficeSection.controls.impersonateAgentNumber.setValue(impersonateAgentNumber);
    }

    if (specialContractTerms) {
      this._backofficeSection.controls.specialContractTerms.setValue(true);
      this._backofficeSection.controls.specialContractTermsText.setValue(specialContractTerms);
    }

    this.backofficeSection.markAllAsTouched();

    if (!this.backofficeSection.invalid) {
      this.store.dispatch(fromProcess.actions.setSendBackOfficeData({ sendBackOfficeData: true }));
    }
  }

  usePolicyData(data: LoadPolicyDataResult) {
    switch (data.codeName) {
      case ProcessType.TRAVELINPL: {
        this.patchTravelInPlForm(data);
        this.patchPolicyholderForm(data);
        break;
      }
      case ProcessType.TRIPABROAD: {
        this.patchTripAbroadForm(data);
        this.patchPolicyholderForm(data);
        break;
      }
      case ProcessType.RESIGNATION: {
        this.patchResignationForm(data);
        this.patchPolicyholderForm(data);
        break;
      }
    }
  }

  private patchResignationForm(apiData: LoadPolicyDataResult) {
    if (!apiData.path?.resignation) {
      return;
    }

    const { travelDates, numberOfInsureds, totalTravelPrice, destination, travelPurchaseDate } = apiData.path.resignation;

    this.resignation.patchValue({
      destination: destination?.countryId?.toString(),
      travelDates: {
        dateFrom: travelDates?.dateFrom ? new Date(travelDates.dateFrom).toDateString() : null,
        dateTo: travelDates?.dateTo ? new Date(travelDates.dateTo).toDateString() : null,
      },
      travelCost: [{ travelPurchaseDate: new Date(travelPurchaseDate).toDateString(), travelCost: totalTravelPrice }],
      numberOfInsureds,
      totalTravelPrice,
    });
  }

  private patchTripAbroadForm(apiData: LoadPolicyDataResult) {
    const { tripAbroad } = apiData.path || {};

    if (!tripAbroad) {
      return;
    }

    const { travelDates, travelersPerBirthYear, coverages, destination, isPolandIsCurrentResiding } = tripAbroad;

    this.tripAbroad.patchValue({
      destination: destination?.countryId?.toString(),
      travelDates: {
        annual: travelDates?.annual,
        dateFrom: travelDates?.dateFrom ? new Date(travelDates.dateFrom).toDateString() : null,
        dateTo: travelDates?.dateTo ? new Date(travelDates.dateTo).toDateString() : null,
        with60DaysReturns: travelDates?.with60DaysReturns,
      },
      numberOfInsureds: travelersPerBirthYear?.numberOfInsureds,
      insuredBirthYear: travelersPerBirthYear?.insuredBirthYear.map((insured) => insured.birthYear),
      isPolandIsCurrentResiding,
      winterSportsHiRisk: coverages?.winterSportsHiRisk ? coverages?.winterSportsHiRisk.selected : null,
      winterSportsHiRiskForAllInsureds: coverages?.winterSportsHiRisk?.specifying?.winterSportsHiRiskForAllInsureds,
      personsWithWinterSportsHiRisk: coverages?.winterSportsHiRisk?.specifying?.personsWithWinterSportsHiRisk?.map(
        (person) => person.selected,
      ),
      withExtremeSports: coverages?.extremeSports ? coverages?.extremeSports?.selected : null,
      extremeSportsForAllInsureds: coverages?.extremeSports?.specifying?.extremeSportsForAllInsureds,
      personsWithExtremeSports: coverages?.extremeSports?.specifying?.personsWithExtremeSports?.map((person) => person?.selected ?? false),
    });
  }

  private patchTravelInPlForm(apiData: LoadPolicyDataResult) {
    const { travelInPL } = apiData.path || {};

    if (!travelInPL) {
      return;
    }

    const { travelDates, travelersPerBirthYear, coverages } = travelInPL;

    this.travelInPl.patchValue({
      travelDates: {
        dateFrom: travelDates?.dateFrom ? new Date(travelDates.dateFrom).toDateString() : null,
        dateTo: travelDates?.dateTo ? new Date(travelDates.dateTo).toDateString() : null,
      },
      numberOfInsureds: travelersPerBirthYear?.numberOfInsureds,
      insuredBirthYear: travelersPerBirthYear?.insuredBirthYear.map((insured) => insured.birthYear),
      winterSportsHiRisk: coverages?.winterSportsHiRisk.selected,
      winterSportsHiRiskForAllInsureds: coverages?.winterSportsHiRisk?.specifying?.winterSportsHiRiskForAllInsureds,
      personsWithWinterSportsHiRisk: coverages?.winterSportsHiRisk?.specifying?.personsWithWinterSportsHiRisk?.map(
        (person) => person.selected,
      ),
      withExtremeSports: coverages?.extremeSports?.selected,
      extremeSportsForAllInsureds: coverages?.extremeSports?.specifying?.extremeSportsForAllInsureds,
      personsWithExtremeSports: coverages?.extremeSports?.specifying?.personsWithExtremeSports?.map((person) => person.selected),
    });
  }

  private patchPolicyholderForm(apiData: LoadPolicyDataResult) {
    const phoneNumberSlice = 3;

    const { isCompany, firstName, lastName, pesel, isForeigner, passport, companyName, nip, regon, phoneNumber, emailAddress, address } =
      apiData.policyholder;

    this.policyholder.patchValue({
      isCompany,
      firstName,
      lastName,
      pesel,
      isForeigner,
      passport,
      companyName,
      nip,
      regon,
      phoneNumber: phoneNumber ? phoneNumber.slice(phoneNumberSlice) : null,
      emailAddress,
      postCode: address?.postCode,
      city: address?.city,
      street: address?.street,
      houseNumber: address?.houseNumber,
      flatNumber: address?.flatNumber,
    });

    if (apiData.insureds) {
      this.updatePolicyholderInsureds(apiData.insureds);
    }
  }

  private updatePolicyholderInsureds(data: InsuredPayload[]) {
    const dateOfBirthSplitted = 0;

    this._policyholder.controls.insureds.clear();
    data.forEach((singleInsured) => {
      const insured = new FormGroup<{
        firstName: FormControl<string | null>;
        lastName: FormControl<string | null>;
        dateOfBirth: FormControl<string | null>;
        toRemove: FormControl<boolean | null>;
      }>({
        firstName: new FormControl<string>(singleInsured.firstName, [Validators.required, FirstNameValidator]),
        lastName: new FormControl<string>(singleInsured.lastName, [Validators.required, LastNameValidator]),
        dateOfBirth: new FormControl<string>(new Date(singleInsured.dateOfBirth).toISOString().split('T')[dateOfBirthSplitted], [
          Validators.required,
          BirthDateValidator,
        ]),
        toRemove: new FormControl<boolean>(false),
      });
      this.policyholder.controls.insureds.push(insured);
    });
  }

  private setInsuredsBirthYear(controls: Array<FormControl<number | null>>, data: InsuredImportResponse) {
    controls.forEach((control, index) => {
      if (data.records) {
        const { dateOfBirth } = data.records[index].data;
        const yearOfBirth = dateOfBirth ? new Date(dateOfBirth).getFullYear() : null;

        if (yearOfBirth) {
          control.setValue(yearOfBirth);
        }
      }
    });
  }

  private setInsuredsData(
    controls: Array<
      FormGroup<{
        firstName: FormControl<string | null>;
        lastName: FormControl<string | null>;
        dateOfBirth: FormControl<string | null>;
        toRemove: FormControl<boolean | null>;
      }>
    >,
    data: InsuredImportResponse,
  ) {
    controls.forEach((control, index) => {
      if (data.records) {
        const { firstName, lastName, dateOfBirth } = data.records[index].data;

        if (firstName) {
          control.controls.firstName.setValue(firstName);
        }
        if (lastName) {
          control.controls.lastName.setValue(lastName);
        }
        if (dateOfBirth) {
          control.controls.dateOfBirth.setValue(dateOfBirth);
        }
      }
    });
  }

  private implementInvalidStep2() {
    this._travelInPl.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      this._travelInPlInvalid$.next(this._travelInPl.invalid);
    });

    this._tripAbroad.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      this._tripAbroadInvalid$.next(this._tripAbroad.invalid);
    });

    this._resignation.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      this._resignationInvalid$.next(this._resignation.invalid);
    });
  }

  private implementInvalidStep4() {
    this._policyholder.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      this._policyholderInvalid$.next(this._policyholder.invalid);
    });
  }

  private implementAgentSplitCommisionsForm() {
    const splits = (this.persistStorage.getJson(PersistStorageKeys.FormsAgentCommissionSplits) as PersistStorageAgentCommissionSplits)
      ?.splits;

    if (!splits) {
      return;
    }

    Array(splits.length - AMOUNT_TO_REMOVE)
      .fill(null)
      .forEach(() =>
        this._agentCommissionSplits.controls.splits.push(
          new FormGroup({
            agentNumber: new FormControl('', {
              validators: [Validators.required],
              asyncValidators: [
                (control: AbstractControl<string, string>) => this.asyncAgentNumberValidator.validate(control, 'agentData'),
              ],
              updateOn: 'blur',
            }),
            commissionPercent: new FormControl(MAIN_AGENT_COMMISSION),
            agentData: new FormControl(''),
          }),
        ),
      );
  }

  private implementPolicyholderForm() {
    const update = () => {
      this._processKind$.pipe(first()).subscribe((processKind) => {
        let numberOfInsureds = NaN;
        let insuredBirthDates: (number | null)[] = [];

        switch (processKind) {
          case ProcessType.TRAVELINPL:
            numberOfInsureds = Number(this.travelInPl.controls.numberOfInsureds.value);
            insuredBirthDates = this.travelInPl.controls.insuredBirthYear.value;
            if (numberOfInsureds === SINGLE_INSURED) {
              this.travelInPl.controls.winterSportsHiRiskForAllInsureds.setValue(Boolean(this.travelInPl.value.winterSportsHiRisk));
              this.travelInPl.controls.extremeSportsForAllInsureds.setValue(Boolean(this.travelInPl.value.withExtremeSports));
            }
            break;
          case ProcessType.TRIPABROAD:
            numberOfInsureds = Number(this.tripAbroad.controls.numberOfInsureds.value);
            insuredBirthDates = this.tripAbroad.controls.insuredBirthYear.value;
            if (numberOfInsureds === SINGLE_INSURED) {
              this.tripAbroad.controls.winterSportsHiRiskForAllInsureds.setValue(Boolean(this.tripAbroad.value.winterSportsHiRisk));
              this.tripAbroad.controls.extremeSportsForAllInsureds.setValue(Boolean(this.tripAbroad.value.withExtremeSports));
            }
            break;
          case ProcessType.RESIGNATION:
            numberOfInsureds = Number(this.resignation.controls.numberOfInsureds.value);
            break;
        }

        if (isNaN(numberOfInsureds)) {
          return;
        }

        const insuredTmpValues = this._policyholder.controls.insureds.value;
        this._policyholder.controls.insureds.clear();

        Array(numberOfInsureds)
          .fill(null)
          .forEach((_, index) => {
            let personToPatch: Partial<{
              firstName: string | null;
              lastName: string | null;
              dateOfBirth: string | null;
              toRemove: boolean | null;
            }> | null = null;
            if (
              insuredTmpValues[index]?.dateOfBirth &&
              (!insuredBirthDates.length || moment(insuredTmpValues[index].dateOfBirth).year() === insuredBirthDates[index])
            ) {
              personToPatch = insuredTmpValues[index];
            }

            const insureds = new FormGroup<{
              firstName: FormControl<string | null>;
              lastName: FormControl<string | null>;
              dateOfBirth: FormControl<string | null>;
              toRemove: FormControl<boolean | null>;
            }>({
              firstName: new FormControl<string>(personToPatch?.firstName ?? '', [Validators.required, FirstNameValidator]),
              lastName: new FormControl<string>(personToPatch?.lastName ?? '', [Validators.required, LastNameValidator]),
              dateOfBirth: new FormControl<string>(personToPatch?.dateOfBirth ?? '', [Validators.required, BirthDateValidator]),
              toRemove: new FormControl<boolean>(false),
            });

            this._policyholder.controls.insureds.push(insureds);
          });
      });
    };

    const updateValidators = (isCompany: boolean | null, isForeigner: boolean | null) => {
      if (isCompany) {
        this.policyholder.controls.companyName.setValidators([Validators.required]);
        this.policyholder.controls.firstName.setValidators([]);
        this.policyholder.controls.lastName.setValidators([]);
        this.policyholder.controls.pesel.setValidators([]);
        this.policyholder.controls.pesel.setAsyncValidators([]);
        this.policyholder.controls.passport.setValidators([]);
        this.policyholder.controls.nip.setValidators([Validators.required, NipValidator]);
        this.policyholder.controls.regon.setValidators([RegonValidator]);
      } else {
        this.policyholder.controls.companyName.setValidators([]);
        this.policyholder.controls.firstName.setValidators([Validators.required, FirstNameValidator]);
        this.policyholder.controls.lastName.setValidators([Validators.required, LastNameValidator]);
        this.policyholder.controls.pesel.setValidators(isForeigner ? [] : [Validators.required, PeselValidator]);
        this.policyholder.controls.passport.setValidators(isForeigner ? [Validators.required, PassportValidator] : []);
        this.policyholder.controls.nip.setValidators([]);
        this.policyholder.controls.nip.setAsyncValidators([]);
        this.policyholder.controls.regon.setValidators([]);
      }
    };

    this.policyholder.controls.isCompany.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((isCompany) => {
      updateValidators(isCompany, this.policyholder.controls.isForeigner.value);
    });

    this.policyholder.controls.isForeigner.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((isForeigner) => {
      updateValidators(this.policyholder.controls.isCompany.value, isForeigner);
    });

    this.travelInPl.controls.numberOfInsureds.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      update();
    });

    this.tripAbroad.controls.numberOfInsureds.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      update();
    });

    this.resignation.controls.numberOfInsureds.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      update();
    });

    updateValidators(this.policyholder.controls.isCompany.value, this.policyholder.controls.isForeigner.value);

    this.travelInPl.controls.winterSportsHiRisk.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((winterSportsHiRisk) => {
      const numberOfInsureds = this.travelInPl.value.numberOfInsureds;
      if (numberOfInsureds === SINGLE_INSURED) {
        this.travelInPl.controls.winterSportsHiRiskForAllInsureds.setValue(winterSportsHiRisk);
      }
    });

    this.tripAbroad.controls.winterSportsHiRisk.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((winterSportsHiRisk) => {
      const numberOfInsureds = this.tripAbroad.value.numberOfInsureds;
      if (numberOfInsureds === SINGLE_INSURED) {
        this.tripAbroad.controls.winterSportsHiRiskForAllInsureds.setValue(winterSportsHiRisk);
      }
    });

    this.travelInPl.controls.withExtremeSports.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((withExtremeSports) => {
      const numberOfInsureds = this.travelInPl.value.numberOfInsureds;
      if (numberOfInsureds === SINGLE_INSURED) {
        this.travelInPl.controls.extremeSportsForAllInsureds.setValue(withExtremeSports);
      }
    });

    this.tripAbroad.controls.withExtremeSports.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((withExtremeSports) => {
      const numberOfInsureds = this.tripAbroad.value.numberOfInsureds;
      if (numberOfInsureds === SINGLE_INSURED) {
        this.tripAbroad.controls.extremeSportsForAllInsureds.setValue(withExtremeSports);
      }
    });

    this.policyholder.controls.companyName.valueChanges
      .pipe(takeUntil(this._ngDestroy$), debounceTime(VALIDATION_DEBOUNCE_TIME))
      .subscribe((companyName) => {
        if (companyName && this.policyholder.controls.nip.value) {
          this.policyholder.controls.nip.markAsTouched();
          this.policyholder.controls.nip.updateValueAndValidity();
        }
      });
  }

  private implementUpdateAnnualControls() {
    this.tripAbroad.controls.travelDates.controls.dateFrom.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((dateFrom) => {
      if (this.tripAbroad.value.travelDates?.annual) {
        return;
      }

      const currentDateTo = !!this.tripAbroad.value.travelDates?.dateTo ? new Date(this.tripAbroad.value.travelDates.dateTo) : null;
      const dateTo = dateFrom ? new Date(dateFrom) : null;
      if (dateTo) {
        dateTo.setDate(dateTo.getDate() + TRAVEL_DATE_TO_DEFAULT_INCREMENT_DAYS);
      }

      if (!!dateTo && (!currentDateTo || currentDateTo.getTime() < dateTo.getTime())) {
        this.tripAbroad.controls.travelDates.controls.dateTo.setValue(dateTo ? toDateString(dateTo) : null);
      }
    });

    this.travelInPl.controls.travelDates.controls.dateFrom.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((dateFrom) => {
      const currentDateTo = !!this.travelInPl.value.travelDates?.dateTo ? new Date(this.travelInPl.value.travelDates.dateTo) : null;
      const dateTo = dateFrom ? new Date(dateFrom) : null;
      if (dateTo) {
        dateTo.setDate(dateTo.getDate() + TRAVEL_DATE_TO_DEFAULT_INCREMENT_DAYS);
      }
      if (!!dateTo && (!currentDateTo || currentDateTo.getTime() < dateTo.getTime())) {
        this.travelInPl.controls.travelDates.controls.dateTo.setValue(dateTo ? toDateString(dateTo) : null);
      }
    });

    combineLatest([
      this.tripAbroad.controls.travelDates.controls.dateFrom.valueChanges,
      this.tripAbroad.controls.travelDates.controls.annual.valueChanges,
    ])
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(([dateFromString, annual]) => {
        if (annual) {
          this.tripAbroad.controls.travelDates.controls.dateTo.disable();
          if (dateFromString) {
            const dateFrom = new Date(dateFromString);
            const dateTo = moment(dateFrom)
              .add(ANNUAL_INCREMENT_YEARS, 'year')
              .subtract(TRAVEL_DATE_TO_DEFAULT_INCREMENT_DAYS, 'day')
              .toDate();

            const dateToString = toDateString(dateTo);
            this.tripAbroad.controls.travelDates.controls.dateTo.setValue(dateToString);
          } else {
            this.tripAbroad.controls.travelDates.controls.dateTo.setValue(null);
          }
          // Number of insureds
          this.tripAbroad.controls.numberOfInsureds.setValue(NUMBER_OF_INSUREDS_INITIAL_VALUE);

          // Hi risk sports
          this.tripAbroad.controls.winterSportsHiRisk.disable();
          this.tripAbroad.controls.winterSportsHiRisk.setValue(null);
          this.tripAbroad.controls.winterSportsHiRiskForAllInsureds.setValue(null);
          this.tripAbroad.controls.winterSportsHiRiskForAllInsureds.disable();
          this.tripAbroad.controls.personsWithWinterSportsHiRisk.controls.forEach((control) => control.setValue(false));

          // Extreme sports [agent]
          this.tripAbroad.controls.withExtremeSports.disable();
          this.tripAbroad.controls.withExtremeSports.setValue(null);
          this.tripAbroad.controls.extremeSportsForAllInsureds.setValue(null);
          this.tripAbroad.controls.extremeSportsForAllInsureds.disable();
          this.tripAbroad.controls.personsWithExtremeSports.controls.forEach((control) => control.setValue(false));

          return;
        } else {
          this.tripAbroad.controls.withExtremeSports.enable();
          this.tripAbroad.controls.winterSportsHiRisk.enable();
          this.tripAbroad.controls.winterSportsHiRiskForAllInsureds.enable();
          this.tripAbroad.controls.extremeSportsForAllInsureds.enable();
        }

        this.tripAbroad.controls.travelDates.controls.dateTo.enable();
      });

    this.tripAbroad.controls.isPolandIsCurrentResiding.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((value) => {
      switch (value) {
        case false:
          const minDate = new Date();
          minDate.setDate(minDate.getDate() + MIN_INSURANCE_DIFF_AB);

          this._tripAbroad.controls.travelDates.controls.dateFrom.setValue(toDateString(minDate));

          break;
      }
    });

    this.tripAbroad.controls.travelDates.controls.dateFrom.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      if (!this.isPolandIsCurrentResidingVisible) {
        this.tripAbroad.controls.isPolandIsCurrentResiding.setValue(true);
        this.tripAbroad.controls.isPolandIsCurrentResiding.setValidators([]);
      }

      this.isPolandIsCurrentResidingVisible$.next(this.isPolandIsCurrentResidingVisible);
      this.tripAbroad.controls.isPolandIsCurrentResiding.setValidators([Validators.required]);
    });

    this.tripAbroad.controls.travelDates.controls.annual.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((annual) => {
      if (annual) {
        this.tripAbroad.controls.travelDates.controls.with60DaysReturns.setValidators([Validators.required]);

        return;
      }
      this.tripAbroad.controls.travelDates.controls.with60DaysReturns.setValidators([]);

      const dateFrom = this.tripAbroad.controls.travelDates.controls.dateFrom.value
        ? new Date(this.tripAbroad.controls.travelDates.controls.dateFrom.value)
        : null;

      const dateTo = dateFrom
        ? moment(dateFrom).add(ANNUAL_INCREMENT_YEARS, 'year').subtract(TRAVEL_DATE_TO_DEFAULT_INCREMENT_DAYS, 'day').toDate()
        : null;

      if (dateTo && this.tripAbroad.controls.travelDates.controls.dateTo.value === toDateString(dateTo)) {
        this.tripAbroad.controls.travelDates.controls.dateTo.patchValue(null);
      }
    });
  }

  private implementUpdateInsuredBirthYearControls() {
    const patchInsuredsBirthDate = (insuredBirthYears: (number | string | null)[]) => {
      insuredBirthYears.forEach((birthYear, insuredId) => {
        const dateOfBirth = this.policyholder.controls.insureds.controls[insuredId]?.controls.dateOfBirth.value;
        if (dateOfBirth && new Date(dateOfBirth).getFullYear() !== Number(birthYear)) {
          this.policyholder.controls.insureds.controls[insuredId].controls.dateOfBirth.patchValue(null);
        }
      });
    };

    this._travelInPl.controls.numberOfInsureds.valueChanges
      .pipe(
        takeUntil(this._ngDestroy$),
        switchMap((value) => {
          const insuredBirthYearControls = Array(value)
            .fill(null)
            .map(
              (_, index) =>
                new FormControl<number | null>(this._travelInPl.controls.insuredBirthYear.value[index] || null, [
                  Validators.required,
                  BirthYearValidator,
                ]),
            );

          this._travelInPl.setControl('insuredBirthYear', new FormArray(insuredBirthYearControls));

          const personsWithWinterSportsHiRiskControls = Array(value)
            .fill(null)
            .map(
              (_, index) => new FormControl<boolean | null>(this._travelInPl.controls.personsWithWinterSportsHiRisk.value[index] || null),
            );

          this._travelInPl.setControl('personsWithWinterSportsHiRisk', new FormArray(personsWithWinterSportsHiRiskControls));

          const personsWithExtremeSportsControls = Array(value)
            .fill(null)
            .map((_, index) => new FormControl<boolean | null>(this._travelInPl.controls.personsWithExtremeSports.value[index] || null));

          this._travelInPl.setControl('personsWithExtremeSports', new FormArray(personsWithExtremeSportsControls));

          return this._travelInPl.controls.insuredBirthYear.valueChanges;
        }),
        tap((insuredBirthYear) => {
          patchInsuredsBirthDate(insuredBirthYear);
        }),
      )
      .subscribe();

    this._tripAbroad.controls.numberOfInsureds.valueChanges
      .pipe(
        takeUntil(this._ngDestroy$),
        switchMap((value) => {
          const insuredBirthYearControls = Array(value)
            .fill(null)
            .map(
              (_, index) =>
                new FormControl<number | null>(this._tripAbroad.controls.insuredBirthYear.value[index] || null, [
                  Validators.required,
                  BirthYearValidator,
                ]),
            );

          this._tripAbroad.setControl('insuredBirthYear', new FormArray<FormControl<number | null>>(insuredBirthYearControls));

          const personsWithWinterSportsHiRiskControls = Array(value)
            .fill(null)
            .map(
              (_, index) => new FormControl<boolean | null>(this._tripAbroad.controls.personsWithWinterSportsHiRisk.value[index] || null),
            );

          this._tripAbroad.setControl('personsWithWinterSportsHiRisk', new FormArray(personsWithWinterSportsHiRiskControls));

          const personsWithExtremeSportsControls = Array(value)
            .fill(null)
            .map((_, index) => new FormControl<boolean | null>(this._tripAbroad.controls.personsWithExtremeSports.value[index] || null));

          this._tripAbroad.setControl('personsWithExtremeSports', new FormArray(personsWithExtremeSportsControls));

          return this._tripAbroad.controls.insuredBirthYear.valueChanges;
        }),
        tap((insuredBirthYear) => {
          patchInsuredsBirthDate(insuredBirthYear);
        }),
      )
      .subscribe();
  }

  private implementUpdateTotalTravelPrice() {
    this._resignation.controls.travelCost.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe(() => {
      const sumOfTravelCosts = this._resignation.controls.travelCost.controls
        .map(({ controls }) => controls.travelCost)
        .reduce((acc, curr) => acc + (curr?.value || STARTING_SUM_OF_TRAVEL_COSTS), STARTING_SUM_OF_TRAVEL_COSTS);

      this._resignation.controls.totalTravelPrice.setValue(sumOfTravelCosts);
    });
  }

  private updateTravelCostValidations(resignationLimitPerInsuredPerson: number | undefined, numberOfInsureds?: number | null) {
    if (resignationLimitPerInsuredPerson !== undefined && numberOfInsureds !== null && numberOfInsureds !== undefined) {
      this._resignation.controls.travelCost.clearValidators();
      this._resignation.controls.travelCost.addValidators(SumCostValidator(numberOfInsureds, resignationLimitPerInsuredPerson));
      this._resignation.controls.travelCost.updateValueAndValidity();
    }
  }

  private implementBackofficeSectionValidations() {
    this._backofficeSection.controls.postingOnAgentBehalf.valueChanges.pipe(takeUntil(this._ngDestroy$)).subscribe((value) => {
      const impersonateAgentNumberControl = this._backofficeSection.get('impersonateAgentNumber');
      if (value) {
        // If postingOnAgentBehalf is true, set the async validator
        impersonateAgentNumberControl?.setAsyncValidators([
          (control: AbstractControl<string, string>) => this.asyncAgentNumberValidator.validate(control, 'agentData'),
        ]);
      } else {
        // If postingOnAgentBehalf is false, clear the validators, reset impersonateAgentNumber value and reset splits
        impersonateAgentNumberControl?.clearAsyncValidators();
        impersonateAgentNumberControl?.setValue(null);
        this.resetAgentCommissionSplits();
      }
      impersonateAgentNumberControl?.updateValueAndValidity(); // Update the control's value and validation status
    });
  }

  private implementTravelCostValidations() {
    combineLatest([this._resignationLimitPerInsuredPerson$, this.resignation.controls.numberOfInsureds.valueChanges])
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(([resignationLimitPerInsuredPerson, numberOfInsureds]) => {
        this.updateTravelCostValidations(resignationLimitPerInsuredPerson, numberOfInsureds);
      });

    this._resignationLimitPerInsuredPerson$.pipe(takeUntil(this._ngDestroy$)).subscribe((resignationLimitPerInsuredPerson) => {
      this.updateTravelCostValidations(resignationLimitPerInsuredPerson, this._resignation.value.numberOfInsureds);
    });
  }

  private implementPreserveForm() {
    this.preserveForm(PersistStorageKeys.FormsTravelInPl, this._travelInPl);
    this.preserveForm(PersistStorageKeys.FormsTripAbroad, this._tripAbroad);
    this.preserveForm(PersistStorageKeys.FormsResignation, this._resignation);
    this.preserveForm(PersistStorageKeys.FormsAgreements, this._agreementsForm);
    this.preserveForm(PersistStorageKeys.FormsPolicyholder, this._policyholder);
    this.preserveForm(PersistStorageKeys.FormsBackOfficeSection, this._backofficeSection);
    this.preserveForm(PersistStorageKeys.FormsAgentCommissionSplits, this._agentCommissionSplits);
  }

  private async implementHydrateForm() {
    this._hydrationState$.next(ApiState.pending);
    try {
      await this.hydrateForm(PersistStorageKeys.FormsTravelInPl, this._travelInPl);
      await this.hydrateForm(PersistStorageKeys.FormsTripAbroad, this._tripAbroad);
      await this.hydrateForm(PersistStorageKeys.FormsResignation, this._resignation);
      await this.hydrateForm(PersistStorageKeys.FormsPolicyholder, this._policyholder);
      await this.hydrateForm(PersistStorageKeys.FormsAgreements, this._agreementsForm);
      await this.hydrateForm(PersistStorageKeys.FormsBackOfficeSection, this._backofficeSection);
      await this.hydrateForm(PersistStorageKeys.FormsAgentCommissionSplits, this._agentCommissionSplits);

      this._hydrationState$.next(ApiState.success);
    } catch (e) {
      console.error(e);
      this._hydrationState$.next(ApiState.success); // @TODO: Handle it later
      // this.persistStorage.removeAll();
    }
  }

  private preserveForm(name: PersistStorageKeys, form: FormGroup) {
    combineLatest([this._hydrationState$, form.valueChanges])
      .pipe(
        takeUntil(this._ngDestroy$),
        filter(([state]) => state === ApiState.success),
        debounceTime(PRESERVE_DEBOUNCE_TIME),
      )
      .subscribe(() => {
        const rawValue = form.getRawValue() as Record<string, unknown>;
        this.persistStorage.setJson(name, rawValue);
      });
  }

  private hydrateForm(name: PersistStorageKeys, form: FormGroup) {
    return new Promise((resolve, reject) => {
      try {
        const values = this.persistStorage.getJson<Record<string, unknown>>(name);

        if (!values) {
          return resolve('SKIPPED');
        }

        if (values) {
          setTimeout(() => {
            try {
              this.setFormValues(form, values);
              resolve('OK');
            } catch (e) {
              console.error(e);
              reject(`Error hydrating form ${name}`);
            }
          }, PRESERVE_DEBOUNCE_TIME);
        }
      } catch (e) {
        console.error(e);
        reject(`Error hydrating form ${name}`);
      }
    });
  }

  private setFormValues(form: FormGroup, values: Record<string, unknown>): void {
    Object.entries(values).forEach(([key, value]) => {
      if (!form.controls[key]) {
        form.addControl(key, new FormControl(null));
      }
      if (typeof value === 'object' && value !== null && value !== undefined) {
        this.setFormValues(form.controls[key] as FormGroup, value as Record<string, unknown>);

        return;
      }

      const control = form.controls[key];

      if (!control) {
        throw new Error('Control not exists');
      }

      control.setValue(value);
    });
  }

  private resetPolicyholderInsureds() {
    this._policyholder.controls.insureds.clear();
    const insured = new FormGroup<{
      firstName: FormControl<string | null>;
      lastName: FormControl<string | null>;
      dateOfBirth: FormControl<string | null>;
      toRemove: FormControl<boolean | null>;
    }>({
      firstName: new FormControl<string>('', [Validators.required, FirstNameValidator]),
      lastName: new FormControl<string>('', [Validators.required, LastNameValidator]),
      dateOfBirth: new FormControl<string>('', [Validators.required, BirthDateValidator]),
      toRemove: new FormControl<boolean>(false),
    });
    this._policyholder.controls.insureds.push(insured);
  }

  private resetAgentCommissionSplits() {
    const splitsLength = this._agentCommissionSplits.controls.splits.length;

    if (splitsLength > FIRST_ITEM) {
      for (let i = splitsLength; i > FIRST_ITEM; i--) {
        this.removeMiddleman(i - FIRST_ITEM);
      }
    }
  }

  private removePortFromUrl(url: string): string {
    return url.replace(/([a-zA-Z+.\-]+):\/\/([^\/]+):([0-9]+)\//, '$1://$2/');
  }
}
